import Link from "next/link";
import WellnessIcon from "@/assets/wellness.svg";
import FitnessIcon from "@/assets/fitness.svg";
import HealthIcon from "@/assets/health.svg";
import CurvedArrowDown from "@/assets/curved-arrow-down.svg";
import CurvedArrowUp from "@/assets/curved-arrow-up.svg";
import Image, { StaticImageData } from "next/image";

interface ProcessStep {
  id: string;
  number: string;
  title: string;
  description: string;
  icon: StaticImageData;
}

const processSteps: ProcessStep[] = [
  {
    id: "01",
    number: "01",
    title: "Online Consultation",
    description:
      "Complete our quick online assessment to help us understand your health profile and weight loss goals.",
    icon: WellnessIcon,
  },
  {
    id: "02",
    number: "02",
    title: "Medication Weight Loss Treatment",
    description:
      "Receive your custom weight loss plan designed by medical professionals based on your unique needs.",
    icon: FitnessIcon,
  },
  {
    id: "03",
    number: "03",
    title: "Our Prescribed Medications",
    description:
      "Follow your plan with ongoing support from our medical team and see lasting weight loss results.",
    icon: HealthIcon,
  },
];

export default function ProcessSection() {
  return (
    <section className="py-16 sm:py-20 bg-white relative overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 2xl:px-8 relative">
        {/* Premium Section Header */}
        <div className="text-center mb-16 sm:mb-20">
          <div className="inline-flex items-center space-x-2.5 bg-white/80 backdrop-blur-sm px-5 py-2.5 rounded-full mb-8 border border-gray-200/60 shadow-sm">
            <div className="w-2 h-2 bg-[#7771B8] rounded-full animate-pulse shadow-sm shadow-[#7771B8]/50"></div>
            <span className="text-gray-700 font-semibold text-sm tracking-wide">
              How It Works
            </span>
          </div>

          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-gray-900 mb-6 leading-[1.1] font-[family-name:var(--font-playfair)]">
            Simple steps to{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7771B8] via-[#9d97d8] to-[#7771B8]">
              your success
            </span>
          </h2>

          <p className="text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed font-light">
            Our proven 3-step process makes weight loss simple, safe, and
            sustainable with medical supervision every step of the way.
          </p>
        </div>

        {/* Premium Process Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 relative">
          {processSteps.map((step, index) => (
            <div key={step.id} className="text-center relative group h-full">
              {/* Curved Arrow Connectors */}
              {index < processSteps.length - 1 && (
                <div className="hidden md:block absolute top-24 left-[85%] z-10 w-[120px] lg:w-[180px] h-12 lg:h-16 opacity-60">
                  <Image
                    src={index === 0 ? CurvedArrowDown : CurvedArrowUp}
                    alt="Process arrow"
                    className="w-full h-full object-contain"
                    width={180}
                    height={64}
                  />
                </div>
              )}

              {/* Premium Step Card */}
              <div className="bg-white rounded-2xl shadow-[0_4px_32px_rgba(0,0,0,0.06)] hover:shadow-[0_20px_60px_rgba(119,113,184,0.15)] transition-all duration-700 group-hover:-translate-y-2 h-full flex flex-col overflow-hidden border border-gray-100/80">
                {/* Icon Header */}
                <div className="relative pt-10 pb-6 px-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#7771B8]/10 to-purple-100/50 rounded-2xl mx-auto mb-4 flex items-center justify-center relative border border-[#7771B8]/10 transform group-hover:scale-110 transition-transform duration-500">
                    <Image
                      src={step.icon}
                      alt={step.title}
                      className="w-8 h-8 text-[#7771B8]"
                      width={32}
                      height={32}
                    />

                    {/* Step Number Badge */}
                    <div className="absolute -top-2 -right-2 w-7 h-7 bg-white rounded-full flex items-center justify-center shadow-md border border-gray-100">
                      <span className="text-xs font-semibold text-[#7771B8]">
                        {step.number}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="px-8 pb-10 flex-1 flex flex-col justify-center">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 leading-tight font-[family-name:var(--font-playfair)]">
                    {step.title}
                  </h3>
                  <p className="text-gray-500 leading-relaxed text-base font-light">
                    {step.description}
                  </p>
                </div>
              </div>

              {/* Mobile Arrow */}
              {index < processSteps.length - 1 && (
                <div className="md:hidden flex justify-center my-6">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center border border-gray-200">
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 14l-7 7m0 0l-7-7m7 7V3"
                      />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Premium CTA */}
        <div className="text-center mt-16">
          <Link
            href="/consultation/GUHMJBNGE33R7CJ"
            className="group relative overflow-hidden bg-gradient-to-r from-[#7771B8] via-[#8679c4] to-[#7771B8] bg-size-200 bg-pos-0 hover:bg-pos-100 text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-[#7771B8]/20 hover:shadow-xl hover:shadow-[#7771B8]/30 transition-all duration-500 hover:scale-[1.02] inline-block text-center"
          >
            <span className="relative z-10 flex items-center justify-center gap-2">
              Start your journey today
              <svg
                className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-[#6860a3] to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </Link>
        </div>
      </div>
    </section>
  );
}
